import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './demoTour.css';

/**
 * Step list for the demo guided tour.
 * `route` is optional - when present, navigate there before highlighting.
 */
const STEPS = [
    {
        route: '/scheduler/overview',
        element: '[data-tour="dashboard-stats"]',
        popover: {
            title: 'Dashboard overview',
            description:
                'Start here to see open shifts, shift swaps, requests, clocked-in employees, and training progress for the business.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        route: '/scheduler/overview',
        element: '[data-tour="dashboard-timeline"]',
        popover: {
            title: "Today's schedule",
            description:
                'The timeline gives managers a quick view of who is scheduled, what roles are covered, and where gaps may exist.',
            side: 'top',
            align: 'start',
        },
    },
    {
        route: '/scheduler/schedule/employee',
        element: '[data-tour="schedule-grid"]',
        popover: {
            title: 'Your weekly schedule, your way',
            description:
                'Switch between Month, Employee, Shift, and Jobs views to see the schedule from the angle that fits how you work. Conflicts, overtime, and open shifts are highlighted before you publish.',
            side: 'left',
            align: 'start',
        },
    },
    {
        route: '/scheduler/schedule/employee',
        element: '[data-tour="schedules-list"]',
        popover: {
            title: 'Work on multiple schedules',
            description:
                'Build and edit several schedules at once — split by department or date range — and publish each one individually when it is ready. You can always see which schedule is published and which are drafts, and switch between them as needed.',
            side: 'right',
            align: 'start',
        },
    },
    {
        route: '/scheduler/schedule/employee',
        element: '[data-tour="autofill-open-shifts"]',
        popover: {
            title: 'Fill open shifts faster',
            description:
                'Teamsly can help fill open shifts using employee roles, availability, PTO, preferences, and overtime rules.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        route: '/scheduler/schedule/employee',
        element: '[data-tour="create-schedule"]',
        popover: {
            title: 'Create schedules your way',
            description:
                'Build manually, add required shifts from staffing needs, apply schedule templates, copy a past week, or use AI Auto Schedule.',
            side: 'right',
            align: 'start',
        },
    },
    {
        route: '/scheduler/shifts/shift-types',
        element: '.default-table',
        popover: {
            title: 'Shift Types',
            description:
                'Shift Types are just shift start and end times (like “Lunch 9am-4pm” or “Closing 4pm-11pm”) with a color. When creating a shift, you simply pick a Shift Type instead of entering times every time — this keeps your schedule consistent and much faster to build. The color you pick here will show as the background for that shift in the schedule.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        route: '/scheduler/shifts',
        element: '.user-grid-wrapper .default-table',
        popover: {
            title: 'Staffing Needs',
            description:
                'The shifts in the Staffing Needs section are the minimum required shifts for each role and time per day. These are optional, but help you keep track of coverage. You can assign them to employees in the Schedule Templates tab. Any shifts not assigned will be added to the schedule as open shifts.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        route: '/scheduler/shifts/templates',
        element: '.week-calendar.templates',
        popover: {
            title: 'Schedule Templates',
            description:
                'Schedule Templates are for businesses with repeatable weekly schedules, or employees who have a set weekly pattern. Here, you assign the shifts from Staffing Needs to employees. When creating a schedule, you can choose to apply a template and those assignments are added automatically. This is optional, but it saves time and keeps schedules consistent.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        route: '/scheduler/shifts/shiftalert/create',
        element: '[data-tour="shift-alert-open-shifts"]',
        popover: {
            title: 'Shift Alerts',
            description:
                'Shift Alerts let you send open shifts to qualified employees by text or push notification. Employees can volunteer to take the shift, and managers then choose who to assign from the volunteers. Use the "Send Employees" button to notify your team.',
            side: 'bottom',
            align: 'start',
        },
    },
    {
        route: '/scheduler/requests',
        element: '[data-tour="employee-requests"]',
        popover: {
            title: 'Manage employee requests',
            description:
                'Employees can request shifts, swap shifts, update availability, and request time off. Managers can review and approve in one place.',
            side: 'right',
            align: 'start',
        },
    },
    {
        element: '[data-tour="book-a-call"]',
        popover: {
            title: "You're ready to explore",
            description:
                'This demo is fully clickable - explore the sample data, then book a call when you are ready to see how Teamsly would work for your business.',
            side: 'bottom',
            align: 'end',
        },
    },
];

const TARGET_WAIT_MS = 1500;
const POLL_INTERVAL_MS = 80;

function waitForElement(selector, timeoutMs) {
    return new Promise((resolve) => {
        const existing = document.querySelector(selector);
        if (existing) return resolve(existing);
        const start = Date.now();
        const timer = setInterval(() => {
            const el = document.querySelector(selector);
            if (el) {
                clearInterval(timer);
                resolve(el);
            } else if (Date.now() - start > timeoutMs) {
                clearInterval(timer);
                resolve(null);
            }
        }, POLL_INTERVAL_MS);
    });
}

/**
 * Runs the demo tour. Handles cross-page navigation and missing targets.
 *
 * @param {object} opts
 * @param {object} opts.history - react-router history object
 * @param {Function} [opts.onComplete] - called when the tour finishes naturally
 * @param {Function} [opts.onSkip] - called when the user dismisses early
 */
export function runDemoTour({ history, onComplete, onSkip }) {
    let currentIndex = 0;
    let finished = false;
    let completedNaturally = false;

    const driverObj = driver({
        showProgress: true,
        allowClose: true,
        overlayClickBehavior: 'close',
        popoverClass: 'teamsly-demo-tour',
        progressText: 'Step {{current}} of {{total}}',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Done',
        onDestroyStarted: () => {
            // Driver fires this for both manual close and natural completion.
            // Always allow destroy; mark completion in onDestroyed.
            driverObj.destroy();
        },
        onDestroyed: () => {
            if (finished) return;
            finished = true;
            if (completedNaturally) {
                onComplete && onComplete();
            } else {
                onSkip && onSkip();
            }
        },
    });

    const goToStep = async (idx) => {
        if (idx < 0 || idx >= STEPS.length) {
            completedNaturally = true;
            driverObj.destroy();
            return;
        }
        const step = STEPS[idx];

        // Navigate if needed
        if (step.route && window.location.pathname !== step.route) {
            try { history.push(step.route); } catch (e) { /* ignore */ }
        }

        // Page-intro steps have no element - show a centered popover after a brief settle.
        if (!step.element) {
            // small delay so the route mounts before we show the popover
            await new Promise((r) => setTimeout(r, 250));
            currentIndex = idx;
            const isLast = idx === STEPS.length - 1;
            driverObj.highlight({
                popover: {
                    ...step.popover,
                    showButtons: ['close', 'previous', 'next'],
                    doneBtnText: 'Done',
                    nextBtnText: isLast ? 'Done' : 'Next',
                    onNextClick: () => {
                        if (isLast) {
                            completedNaturally = true;
                            driverObj.destroy();
                        } else {
                            goToStep(currentIndex + 1);
                        }
                    },
                    onPrevClick: () => {
                        goToStep(Math.max(0, currentIndex - 1));
                    },
                    onCloseClick: () => {
                        driverObj.destroy();
                    },
                    progressText: `Step ${idx + 1} of ${STEPS.length}`,
                },
            });
            return;
        }

        const el = await waitForElement(step.element, TARGET_WAIT_MS);
        if (!el) {
            // Skip missing target silently and move on
            const nextIdx = idx + 1;
            if (nextIdx >= STEPS.length) {
                completedNaturally = true;
                driverObj.destroy();
            } else {
                goToStep(nextIdx);
            }
            return;
        }

        currentIndex = idx;
        const isLast = idx === STEPS.length - 1;
        driverObj.highlight({
            element: step.element,
            popover: {
                ...step.popover,
                showButtons: ['close', 'previous', 'next'],
                doneBtnText: 'Done',
                nextBtnText: isLast ? 'Done' : 'Next',
                onNextClick: () => {
                    if (isLast) {
                        completedNaturally = true;
                        driverObj.destroy();
                    } else {
                        goToStep(currentIndex + 1);
                    }
                },
                onPrevClick: () => {
                    goToStep(Math.max(0, currentIndex - 1));
                },
                onCloseClick: () => {
                    driverObj.destroy();
                },
                progressText: `Step ${idx + 1} of ${STEPS.length}`,
            },
        });
    };

    goToStep(0);

    return {
        stop: () => driverObj.destroy(),
    };
}
